import { Fragment } from "react";
import { Calendar, Music2, Play, User, Home } from "lucide-react";
import { TYPE_META, dateLabel } from "../labels";
import type { ContentType, RelatedOption } from "../types";
import { parseMarkdown, type Span } from "./appMarkdown";

// El texto se interpreta con el MISMO markdown que la app (appMarkdown.ts).
function Spans({ spans }: { spans: Span[] }) {
  return (
    <>
      {spans.map((s, k) => {
        if (s.href) return <span key={k} className="font-semibold underline">{s.text}</span>;
        if (s.bold) return <strong key={k}>{s.text}</strong>;
        if (s.italic) return <em key={k}>{s.text}</em>;
        return (
          <Fragment key={k}>
            {s.text.split("\n").map((line, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                {line}
              </Fragment>
            ))}
          </Fragment>
        );
      })}
    </>
  );
}

function embedKind(url: string): "soundcloud" | "youtube" | "vimeo" | "mixcloud" | "spotify" | "link" | "none" {
  const u = url.trim().toLowerCase();
  if (!u) return "none";
  if (/^https?:\/\/(www\.|m\.)?soundcloud\.com\//.test(u)) return "soundcloud";
  if (/^https?:\/\/(www\.|m\.)?(youtube\.com|youtu\.be)\//.test(u)) return "youtube";
  if (/^https?:\/\/(www\.|player\.)?vimeo\.com\//.test(u)) return "vimeo";
  if (/^https?:\/\/(www\.)?mixcloud\.com\//.test(u)) return "mixcloud";
  if (/^https?:\/\/open\.spotify\.com\//.test(u)) return "spotify";
  return /^https?:\/\//.test(u) ? "link" : "none";
}

const SITE: Record<string, string> = { mixcloud: "Mixcloud", spotify: "Spotify", link: "el sitio" };

/**
 * Vista previa «como en el teléfono»: replica la pantalla de la pieza en la app
 * (portada 4:3, tipo, título, fecha, bajada, reproductor o video, texto y «En
 * Mixcore»). Es aproximada: tipografías y medidas pueden variar un poco.
 */
export function PhonePreview({
  type,
  title,
  excerpt,
  coverUrl,
  body,
  sourceUrl,
  publishedAt,
  related,
}: {
  type: ContentType;
  title: string;
  excerpt: string;
  coverUrl: string;
  body: string;
  sourceUrl: string;
  publishedAt: string | null;
  related: { artist: RelatedOption | null; event: RelatedOption | null; club: RelatedOption | null };
}) {
  const meta = TYPE_META[type];
  const kind = embedKind(sourceUrl);
  const date = dateLabel(publishedAt || new Date().toISOString());
  const links = [
    related.artist && { icon: User, label: related.artist.label, sub: "Artista" },
    related.event && { icon: Calendar, label: related.event.label, sub: related.event.sublabel || "Evento" },
    related.club && { icon: Home, label: related.club.label, sub: "Club" },
  ].filter(Boolean) as { icon: typeof User; label: string; sub: string }[];

  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-2xs uppercase tracking-wide text-fg-subtle">Así se verá en la app</span>
      <div
        className="w-[340px] overflow-hidden rounded-[32px] border-[6px] border-neutral-900 bg-white text-[#141416] shadow-xl"
        style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
      >
        <div className="h-[620px] overflow-y-auto">
          <div className="w-full" style={{ aspectRatio: "4 / 3" }}>
            {coverUrl ? (
              <img src={coverUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full" style={{ background: `linear-gradient(135deg, ${meta.gradient[0]}, ${meta.gradient[1]})` }} />
            )}
          </div>
          <div className="flex flex-col gap-3 px-4 pb-8 pt-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: meta.color }}>
              {meta.label}
            </div>
            <h1
              className="text-[25px] font-extrabold leading-[1.15] tracking-[-0.02em] text-[#0B0B12]"
              style={{ fontFamily: "Sora, 'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              {title || "Título de la pieza"}
            </h1>
            <div className="text-[12px] text-[#6B6B76]">Mixcore · {date}</div>
            {excerpt ? <p className="text-[15px] font-semibold leading-[1.45] text-[#141416]">{excerpt}</p> : null}

            {kind === "soundcloud" && (
              <div className="flex items-center gap-3 rounded-2xl bg-[#0B0B12] p-3 text-white">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#0B0B12]">
                  <Play size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-bold">{type === "MIX" ? "Escucha el set" : "Escucha el tema"}</div>
                  <div className="text-[11px] text-white/70">Suena en la app desde SoundCloud</div>
                </div>
                <Music2 size={16} className="ml-auto text-white/60" />
              </div>
            )}
            {(kind === "youtube" || kind === "vimeo") && (
              <div className="flex w-full items-center justify-center rounded-xl bg-black text-white" style={{ aspectRatio: "16 / 9" }}>
                <div className="flex items-center gap-2 text-[12px] text-white/80">
                  <Play size={16} /> Video de {kind === "youtube" ? "YouTube" : "Vimeo"}
                </div>
              </div>
            )}
            {(kind === "mixcloud" || kind === "spotify" || kind === "link") && (
              <div className="self-start rounded-full border border-[#DCDCE3] px-3 py-1.5 text-[12px] font-bold">
                Abrir en {SITE[kind]}
              </div>
            )}

            {body ? (
              <div className="flex flex-col gap-3 text-[15px] leading-[1.6] text-[#141416]">
                {parseMarkdown(body).map((b, i) => {
                  if (b.type === "h1") return <h2 key={i} className="mt-2 text-[20px] font-extrabold leading-tight"><Spans spans={b.spans} /></h2>;
                  if (b.type === "h2") return <h3 key={i} className="mt-1 text-[17px] font-bold leading-tight"><Spans spans={b.spans} /></h3>;
                  if (b.type === "list") {
                    return (
                      <ul key={i} className="ml-5 list-disc space-y-1">
                        {b.items.map((spans, j) => <li key={j}><Spans spans={spans} /></li>)}
                      </ul>
                    );
                  }
                  if (b.type === "quote") {
                    return (
                      <blockquote key={i} className="border-l-[3px] pl-3 italic text-[#5A5A64]" style={{ borderColor: meta.color }}>
                        <Spans spans={b.spans} />
                      </blockquote>
                    );
                  }
                  return <p key={i}><Spans spans={b.spans} /></p>;
                })}
              </div>
            ) : null}

            {links.length > 0 && (
              <div className="mt-2 flex flex-col gap-2">
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#6B6B76]">En Mixcore</div>
                {links.map((l) => (
                  <div key={l.sub + l.label} className="flex items-center gap-3 rounded-2xl border border-[#EDEDF1] p-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#F7F7F5]">
                      <l.icon size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-[14px] font-bold">{l.label}</div>
                      <div className="truncate text-[11px] text-[#6B6B76]">{l.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <p className="max-w-[340px] text-center text-2xs text-fg-subtle">
        El texto admite títulos (# y ##), listas con guion, citas (&gt;), **negritas**, *cursivas* y enlaces. Las imágenes no se muestran y las tablas o el HTML quedan como texto.
      </p>
    </div>
  );
}
